/**
 * Questionnaires Page - Unified view with Templates and Supplier Responses tabs
 *
 * This page combines:
 * - Tab 1: Templates - Admin configuration for questionnaire templates
 * - Tab 2: Supplier Responses - Operational tracking of supplier questionnaire completion
 *
 * Addresses client requirement to have both template management AND operational
 * supplier response tracking accessible from the Questionnaires menu.
 *
 * Both of those are the CUSTOMER's jobs, and the page had no role awareness, so
 * a supplier saw the customer's page verbatim: a "Templates" tab for authoring
 * questionnaires they cannot author (the API correctly returns none, so it was
 * an always-empty tab), and their own submissions labelled "Supplier Responses"
 * as though they were tracking someone else. Suppliers now get the one tab that
 * means something to them, named from their side.
 *
 * Audit Lite: a third customer-side tab, Audits — the customer's own audits of
 * its suppliers and the actions they raised. Shown only when the tenant has
 * Audit Lite switched on; never to suppliers.
 */
import { useSearchParams } from 'react-router-dom';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ClipboardCheck, ClipboardList, FileText, Users } from 'lucide-react';
import { useIsSupplier } from '../hooks/usePortalMode';
import AuditsPanel from './audits/AuditsPanel';

// Import the existing components as tab content
import QualificationTemplates from './QualificationTemplates';
import SupplierQualifications from './SupplierQualifications';
// CR-5865: suppliers get every questionnaire assigned to them, from both
// assignment systems, rather than the customer's response-tracking table.
import SupplierQuestionnairesPanel from './supplier-questionnaires/SupplierQuestionnairesPanel';

export default function Questionnaires() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSupplier = useIsSupplier();
  const { auditLiteEnabled } = useMenuSettings();
  const showAudits = auditLiteEnabled && !isSupplier;
  const requested = searchParams.get('tab');
  const fallback = isSupplier ? 'responses' : 'templates';
  // ?tab=audits while Audit Lite is off (or for a supplier) falls back.
  const activeTab = requested === 'audits' && !showAudits ? fallback : (requested || fallback);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="px-6 pt-6">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Questionnaires</h1>
        </div>
        <p className="text-muted-foreground">
          {isSupplier
            ? 'Questionnaires your customers have asked you to complete'
            : 'Manage questionnaire templates and track supplier completion status'}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="px-6 border-b">
          <TabsList className="h-12 bg-transparent p-0 gap-4">
            {/* Customers build templates before they chase responses, so
                Templates leads for them. Suppliers have no Templates tab at
                all, so their single tab stays first either way. */}
            {!isSupplier && (
              <TabsTrigger
                value="templates"
                className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
              >
                <FileText className="h-4 w-4 mr-2" />
                Templates
              </TabsTrigger>
            )}
            <TabsTrigger
              value="responses"
              className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
            >
              <Users className="h-4 w-4 mr-2" />
              {isSupplier ? 'My Questionnaires' : 'Supplier Responses'}
            </TabsTrigger>
            {showAudits && (
              <TabsTrigger
                value="audits"
                className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
              >
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Audits
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Tab Content */}
        {!isSupplier && (
          <TabsContent value="templates" className="mt-0">
            <QualificationTemplates />
          </TabsContent>
        )}

        <TabsContent value="responses" className="mt-0">
          {isSupplier ? <SupplierQuestionnairesPanel /> : <SupplierQualifications />}
        </TabsContent>

        {showAudits && (
          <TabsContent value="audits" className="mt-0 px-6 pb-6">
            <AuditsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
